import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHelpButton from '../components/help/PageHelpButton';
import { getClients, createClient, updateClient, deleteClient, exportClientsExcel, getInvoiceTemplates } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Trash2,
  Building2,
  FileSpreadsheet,
  Upload,
  Link as LinkIcon,
  Palette,
  Sparkles,
  ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import PortalAccessDialog from '../components/portal/PortalAccessDialog';

export const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [designTemplates, setDesignTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('clients:sortBy') || 'name_asc'; } catch { return 'name_asc'; }
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [portalClient, setPortalClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'España',
    tax_id: '',
    template_id: ''
  });

  useEffect(() => {
    loadClients();
    getInvoiceTemplates().then(({ data }) => setDesignTemplates(data || [])).catch(() => {});
  }, []);

  const loadClients = async () => {
    try {
      const { data } = await getClients();
      setClients(data);
    } catch (e) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Cliente actualizado');
      } else {
        await createClient(formData);
        toast.success('Cliente creado');
      }
      loadClients();
      closeModal();
    } catch (e) {
      toast.error('Error al guardar cliente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    try {
      await deleteClient(id);
      toast.success('Cliente eliminado');
      loadClients();
    } catch (e) {
      toast.error('Error al eliminar cliente');
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        postal_code: client.postal_code || '',
        country: client.country || 'España',
        tax_id: client.tax_id || '',
        template_id: client.template_id || ''
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'España',
        tax_id: '',
        template_id: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const filteredClients = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const out = clients.filter((c) => {
      if (!q) return true;
      const hay = `${c.name || ''} ${c.email || ''} ${c.tax_id || ''} ${c.phone || ''} ${c.city || ''} ${c.address || ''} ${c.contact_person || ''} ${c.notes || ''}`.toLowerCase();
      return hay.includes(q);
    });
    const cmp = (a, b, key, dir) => {
      const va = a?.[key] ?? '';
      const vb = b?.[key] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), 'es', { numeric: true });
    };
    const [field, direction] = sortBy.endsWith('_asc') ? [sortBy.replace('_asc', ''), 1] : [sortBy.replace('_desc', ''), -1];
    return [...out].sort((a, b) => cmp(a, b, field, direction));
  }, [clients, searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Clientes</h1>
            <p className="text-stone-500 text-sm mt-1">Gestiona tu cartera de clientes</p>
          </div>
          <PageHelpButton moduleKey="clients" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Link to="/import?entity=clients">
            <Button variant="outline" className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto" data-testid="import-clients-button">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
          </Link>
          <Button variant="outline" className="rounded-xl" onClick={async () => { try { toast.info('Exportando clientes...'); await exportClientsExcel(); toast.success('Clientes exportados'); } catch { toast.error('Error al exportar clientes'); } }} data-testid="export-clients-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto"
            data-testid="add-client-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Buscar por nombre, NIF, email, ciudad, teléfono…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="search-clients-input"
          />
        </div>
        <div className="relative sm:w-56">
          <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
          <Select
            value={sortBy}
            onValueChange={(v) => {
              setSortBy(v);
              try { localStorage.setItem('clients:sortBy', v); } catch {}
            }}
          >
            <SelectTrigger className="pl-10 rounded-xl" data-testid="clients-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Nombre (A→Z)</SelectItem>
              <SelectItem value="name_desc">Nombre (Z→A)</SelectItem>
              <SelectItem value="city_asc">Ciudad (A→Z)</SelectItem>
              <SelectItem value="created_at_desc">Más recientes primero</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client, index) => (
            <div 
              key={client.id} 
              className={`bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 card-hover animate-fade-in-up stagger-${(index % 5) + 1}`}
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-stone-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">{client.name}</h3>
                    {client.tax_id && (
                      <p className="text-xs text-stone-500 font-mono">{client.tax_id}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/clients/${client.id}/360`}
                    className="p-1.5 hover:bg-violet-50 rounded-xl transition-colors"
                    title="Ver 360 del cliente"
                    data-testid={`view-360-${client.id}`}
                  >
                    <Sparkles className="w-4 h-4 text-violet-600" />
                  </Link>
                  <button
                    onClick={() => setPortalClient(client)}
                    className="p-1.5 hover:bg-orange-50 rounded-xl transition-colors"
                    title="Portal del cliente"
                    data-testid={`portal-client-${client.id}`}
                  >
                    <LinkIcon className="w-4 h-4 text-[#FF6B4A]" />
                  </button>
                  <button
                    onClick={() => openModal(client)}
                    className="p-1.5 hover:bg-stone-100 rounded-xl transition-colors"
                    data-testid={`edit-client-${client.id}`}
                  >
                    <Edit2 className="w-4 h-4 text-stone-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="p-1.5 hover:bg-red-50 rounded-xl transition-colors"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {client.email && (
                  <div className="flex items-center gap-2 text-stone-600">
                    <Mail className="w-4 h-4 text-stone-400" />
                    <span>{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-stone-600">
                    <Phone className="w-4 h-4 text-stone-400" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {(client.city || client.address) && (
                  <div className="flex items-center gap-2 text-stone-600">
                    <MapPin className="w-4 h-4 text-stone-400" />
                    <span>{[client.address, client.city].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {client.custom_fields && Object.keys(client.custom_fields).length > 0 && (
                  <details className="pt-2 border-t border-stone-100">
                    <summary className="text-[10px] uppercase font-bold text-stone-500 cursor-pointer hover:text-[#FF6B4A] transition-colors">
                      Campos personalizados ({Object.keys(client.custom_fields).length})
                    </summary>
                    <div className="mt-2 space-y-1">
                      {Object.entries(client.custom_fields).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-[11px]">
                          <span className="font-medium text-stone-500 min-w-[100px] truncate">{k}:</span>
                          <span className="text-stone-700 break-all">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">No hay clientes</h3>
          <p className="text-stone-500 mb-4">Añade tu primer cliente para empezar</p>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="add-first-client-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir Cliente
          </Button>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre / Razón Social *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">NIF/CIF</Label>
              <Input
                id="tax_id"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="B12345678"
                data-testid="client-taxid-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl"
                  data-testid="client-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-xl"
                data-testid="client-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="rounded-xl"
                  data-testid="client-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">Código Postal</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="rounded-xl"
                  data-testid="client-postal-input"
                />
              </div>
            </div>

            {/* Default invoice design template for this client */}
            {designTemplates.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="template_id" className="flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5 text-violet-600" />
                  Plantilla de diseño PDF (opcional)
                </Label>
                <Select
                  value={formData.template_id || '__default__'}
                  onValueChange={(v) => setFormData({ ...formData, template_id: v === '__default__' ? '' : v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="client-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Usar plantilla por defecto del usuario</SelectItem>
                    {designTemplates.map(tpl => (
                      <SelectItem key={tpl.id} value={tpl.id}>
                        {tpl.name}{tpl.is_default ? ' ⭐' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">Todas las facturas de este cliente usarán esta plantilla automáticamente.</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="save-client-button"
              >
                {editingClient ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PortalAccessDialog
        open={!!portalClient}
        onOpenChange={(o) => !o && setPortalClient(null)}
        client={portalClient}
      />
    </div>
  );
};

export default ClientsPage;
